import React, {useCallback, useEffect, useState} from 'react';
import cx from 'classnames';
import styles from './styles.module.scss';
import {
    Button,
    ChooseDropdown,
    DropdownItem,
    DropdownSelectTrigger,
    DropdownV2,
    Flex,
    Icon,
    Link,
    PriorityIcon,
    Text,
    ToggleSwitch,
    useKeyboardNavigation
} from '@priority-software/priority-style-react';
import {Form} from '../../../../stores/Form';
import {AdvancedSearches} from '../../../Form/components/AdvancedSearch/AdvancedSearches';
import {ReactComponent as DeliveryShipping} from '../../../../images/local_shipping.svg';
import {isRTL} from '../../../../Localization';
import {texts} from '../../texts';
import {TriggerButton} from '../TriggerButton';
import {startEntity} from '../../../../_shared/GWTBridge/invoke/genPage';
import {ActivationSource} from '../../../../constants/ActivationSource';
import {RouteType} from '../../types';
import {StatusIcon} from '../../../Form/components/StatusIcon';
import {ADPFetchMax, DataTestIds, DLVTRACKS, EditingFieldsType} from '../../constants';
import {updateStatus} from '../../actions/action';
import {DropdownItemData} from '@priority-software/priority-style-react/dist/components/DropdownV2/ChooseDropdown/ChooseDropdown';
import {useAvailableStatusesContext} from '../../hook/useAvailableStatusesContext';
import {ModernTooltip} from '../../../../components/Common/Tooltip/ModernTooltip';
import {updateRouteShowType} from '../../services/serverDataStore';
import {OptimizationDialogOpenValue} from '../OptimizationDialog';
import classNames from 'classnames';

type Props = {
    componentClasses?: string;
    form: Form;
    portalId?: string;
    setIsScreenLocked: (isLocked: boolean) => void;
    selectedRoutes: RouteType[];
    onOpenEditDialog: (type: EditingFieldsType) => void;
    isTableOptionDisabled?: boolean;
    toggleHideRoutes: () => void;
    isShowAll: boolean;
    setExpandMode: (showTotals: boolean) => void;
    docTypeFilter: DropdownItemData[];
    setDocTypeFilter: (filter: DropdownItemData[]) => void;
    unselectAllRoutes: () => void;
    isShowAllTotals: boolean;
    onToggleShowTotalsForAll: () => void;
    zIndex: number;
    recordsCounter: number;
    setOptimizationDialogOpenValue: (value: OptimizationDialogOpenValue) => void;
};

enum SettingsValue {
    totals = 'totals',
    title = 'title',
    list = 'list',
    toggleTotals = 'toggleTotals'
}

enum ExpandValue {
    Collapse = 'Collapse',
    ShowTotals = 'ShowTotals'
}

const settingsMenuData: DropdownItemData[] = [
    {label: texts.select_fields_title, value: SettingsValue.title},
    {label: texts.select_fields_totals, value: SettingsValue.totals},
    {label: texts.settings_show_totals, value: SettingsValue.toggleTotals},
    {label: texts.select_fields_list, value: SettingsValue.list}
];

const expandMenuData: DropdownItemData[] = [
    {label: texts.collapsed_and_totals, value: ExpandValue.ShowTotals},
    {label: texts.collapsed, value: ExpandValue.Collapse}
];

const docTypeMenuData: DropdownItemData[] = [
    {label: texts.orders, value: 'O'},
    {label: texts.documents, value: ''}
];

export const StatusItem = ({statuses, item}) => {
    return (
        <Flex justifyContent="flex-start">
            <StatusIcon statuses={statuses} status={item.value} />
            <Text wrap={false} componentClasses={styles.status}>
                {item.label}
            </Text>
        </Flex>
    );
};

export const RoutesHeader = ({
    componentClasses,
    form,
    portalId,
    setIsScreenLocked,
    selectedRoutes,
    onOpenEditDialog,
    isTableOptionDisabled,
    toggleHideRoutes,
    isShowAll,
    setExpandMode,
    docTypeFilter,
    setDocTypeFilter,
    unselectAllRoutes,
    isShowAllTotals,
    onToggleShowTotalsForAll,
    recordsCounter,
    setOptimizationDialogOpenValue
}: Props) => {
    const [isOpenSettings, setIsOpenSettings] = useState(false);
    const [isOpenExpand, setIsOpenExpand] = useState(false);
    const {statuses: availableStatuses} = useAvailableStatusesContext();
    const {focusedIndex, setFocusedIndex, handleNavigationKeys} = useKeyboardNavigation({
        isOpen: true,
        optionsCount: isOpenExpand ? expandMenuData.length : settingsMenuData.length
    });

    useEffect(() => {
        window.addEventListener('keydown', handleNavigationKeys);
        return () => window.removeEventListener('keydown', handleNavigationKeys);
    }, [handleNavigationKeys, settingsMenuData]);

    const rtl = isRTL();

    const openDlvForm = async () => {
        setIsScreenLocked(true);
        startEntity(DLVTRACKS, null, null, null, ActivationSource.ADP, form.dname);
    };

    const changeStatuses = async (status: DropdownItemData) => {
        setIsScreenLocked(true);
        await updateStatus(form, selectedRoutes, status.value as string);
        setIsScreenLocked(false);
    };

    const handleSettings = () => {
        setIsOpenSettings(prev => !prev);
        setFocusedIndex(0);
    };
    const handleCloseSettings = () => {
        setIsOpenSettings(false);
    };

    const onSelectEditMode = (item: DropdownItemData) => {
        switch (item.value) {
            case SettingsValue.totals: {
                onOpenEditDialog(EditingFieldsType.RouteTileTotals);
                break;
            }
            case SettingsValue.title: {
                onOpenEditDialog(EditingFieldsType.RouteTitle);
                break;
            }
            case SettingsValue.list: {
                onOpenEditDialog(EditingFieldsType.RouteTable);
                break;
            }
        }
        setIsOpenSettings(false);
    };

    const onChangeExpandMode = (item: DropdownItemData) => {
        const showTotals = item.value === ExpandValue.ShowTotals;
        setExpandMode(showTotals);
        setIsOpenExpand(false);
    };

    const handleSelect = (data: DropdownItemData, isSelected: boolean, name?: string) => {
        let values: DropdownItemData[] = [];
        if (isSelected) {
            values = [...docTypeFilter, data];
        } else {
            values = docTypeFilter.filter(item => item.value !== data.value);
        }
        updateRouteShowType(values);
        setDocTypeFilter(values);
    };

    const clearFilters = async () => {
        form?.resetFilters();
        form.records = [];
    };

    const clearSelected = () => {
        unselectAllRoutes();
    };

    const toggleExpandedDialog = () => {
        setIsOpenExpand(prev => !prev);
    };

    const renderToggleSwitch = useCallback(
        item => {
            return (
                <Flex justifyContent="flex-start" style={{gap: '8px'}}>
                    <ToggleSwitch
                        componentClasses={styles.labelButton}
                        checked={isShowAllTotals}
                        onChange={onToggleShowTotalsForAll}
                    />
                    <Text skin="paragraph-2">{item.label}</Text>
                </Flex>
            );
        },
        [isShowAllTotals]
    );

    return (
        <Flex
            direction="column"
            componentClasses={cx(styles.wrapper, styles.contentWrapper, componentClasses)}
        >
            <Flex componentClasses={cx(styles.lightBackground, styles.headerRowPadding)}>
                <div className={styles.delivery}>
                    <DeliveryShipping />
                    <Text skin="paragraph-2">{texts.routes}</Text>
                    <div>{recordsCounter}</div>
                </div>

                <Flex justifyContent="flex-start" componentClasses={styles.regFlexGap}>
                    <div style={{maxWidth: 320}}>
                        <ChooseDropdown
                            isMulti
                            data={docTypeMenuData}
                            onChange={handleSelect}
                            noOptionsText={texts.no_options_text}
                            defaultFocusedIndex={0}
                            maxScrollHeight={320}
                            width={150}
                            value={docTypeFilter.map(item => item.value)}
                            trigger={
                                <div className={styles.dropdownTrigger}>
                                    <DropdownSelectTrigger
                                        beforeValue={
                                            <Text
                                                skin="paragraph-2"
                                                textColor="secondary-500"
                                                wrap={false}
                                            >{`${texts.show_items} (${
                                                docTypeFilter.length
                                                    ? docTypeFilter.length
                                                    : texts.display_all
                                            })`}</Text>
                                        }
                                        direction={isRTL() ? 'rtl' : 'ltr'}
                                    />
                                </div>
                            }
                        />
                    </div>
                    {form && (
                        <AdvancedSearches
                            mainSearchButton
                            currentForm={form}
                            filterButtonSkin="text"
                            usePortal
                            portalSelector={portalId}
                            withShortcut={false}
                            maxResults={ADPFetchMax}
                            tooltipText={texts.saved_searches_tooltip}
                            useSearchNameAsTooltip
                            savedSearchesWrapperCalss={styles.savedSearchesWrapper}
                            setIsScreenLocked={setIsScreenLocked}
                        />
                    )}
                    <ModernTooltip
                        tooltip={texts.clear_search}
                        arrowOffset={8}
                        position="top-start"
                    >
                        <Icon
                            icon={PriorityIcon.clearFilters}
                            hoverable
                            size="small"
                            iconColor="primary-700"
                            onClick={clearFilters}
                        />
                    </ModernTooltip>
                </Flex>
                <Flex justifyContent="flex-end" componentClasses={styles.regFlexGap}>
                    <Button
                        skin="secondary"
                        prefixIcon={PriorityIcon.newAdd}
                        onClick={openDlvForm}
                        dataTestId="new-route-btn"
                    >
                        {texts.new}
                    </Button>
                    <div>
                        <DropdownV2
                            isOpen={isOpenSettings}
                            onClose={handleCloseSettings}
                            width="fullWidth"
                            trigger={
                                <ModernTooltip
                                    tooltip={texts.routes_settings_tooltip}
                                    maxWidth={600}
                                    arrowOffset={8}
                                    position="top-start"
                                    tooltipOffset={8}
                                >
                                    <Icon
                                        icon={PriorityIcon.settings}
                                        hoverable
                                        iconColor="primary-700"
                                        onClick={handleSettings}
                                        dataTestId={DataTestIds.routesHeaderSettingsBtn}
                                    />
                                </ModernTooltip>
                            }
                            usePortal
                            portalSelector={portalId}
                            openingSide={'leftSide'}
                        >
                            {settingsMenuData.map((item, index) => {
                                const isTableOption = item.value === SettingsValue.list;
                                const isItemDisabled = isTableOption && isTableOptionDisabled;
                                const isToggleTotalsSwitch =
                                    item.value === SettingsValue.toggleTotals;
                                return (
                                    <DropdownItem
                                        key={index}
                                        data={item}
                                        onSelect={onSelectEditMode}
                                        isFocused={index === focusedIndex}
                                        itemBorder
                                        clickable={!isToggleTotalsSwitch}
                                        disabled={isItemDisabled}
                                        contentRenderer={
                                            isToggleTotalsSwitch ? renderToggleSwitch : null
                                        }
                                    />
                                );
                            })}
                        </DropdownV2>
                    </div>
                </Flex>
            </Flex>
            <Flex componentClasses={styles.headerRowPadding}>
                <Flex justifyContent="flex-start">
                    <Text startPadding endPadding wrap={false} dataTestId="selected-routes-count">
                        {`${
                            selectedRoutes?.length
                                ? texts.X_routes_selected.replace('X', selectedRoutes.length)
                                : texts.nothing_selected
                        } `}
                    </Text>
                    {selectedRoutes?.length ? (
                        <Link wrapText={false} onClick={clearSelected}>
                            {texts.clear_all_selected}
                        </Link>
                    ) : null}
                </Flex>
                <Flex justifyContent="flex-end" componentClasses={styles.regFlexGap}>
                    <ModernTooltip
                        tooltip={texts.route_optimization_button_tooltip}
                        maxWidth={400}
                        arrowOffset={8}
                        position="top-start"
                        tooltipOffset={8}
                    >
                        <Button
                            dataTestId={DataTestIds.optimizeRouteButton}
                            width={'contentWidth'}
                            componentClasses={classNames(styles.optimizeRouteButton, {
                                [styles.rtl]: rtl
                            })}
                            skin={'aierp'}
                            size={'small'}
                            disabled={selectedRoutes.length === 0}
                            onClick={() =>
                                setOptimizationDialogOpenValue(OptimizationDialogOpenValue.Open)
                            }
                        >
                            {texts.route_optimization_title}
                        </Button>
                    </ModernTooltip>
                    <ChooseDropdown
                        componentClasses={styles.chooseDropdown}
                        isRtl={rtl}
                        itemBorder={false}
                        onChange={changeStatuses}
                        noOptionsText={texts.no_options_text}
                        usePortal
                        portalSelector={portalId}
                        defaultFocusedIndex={0}
                        maxScrollHeight={320}
                        searchPlaceholder={texts.search_placeholder}
                        data={availableStatuses?.map(status => ({
                            label: status.string1,
                            value: status.retval
                        }))}
                        itemContentRenderer={item => (
                            <StatusItem item={item} statuses={form.statuses} />
                        )}
                        trigger={
                            <TriggerButton
                                value={texts.change_status}
                                disabled={!selectedRoutes?.length}
                                dataTestId="change-status-btn"
                            />
                        }
                    />
                    <ModernTooltip
                        tooltip={isShowAll ? texts.show_selected_routes : texts.show_all_routes}
                        maxWidth={600}
                        arrowOffset={8}
                        position="top-start"
                        tooltipOffset={8}
                    >
                        <Icon
                            icon={
                                isShowAll
                                    ? PriorityIcon.showPasswordThin
                                    : PriorityIcon.passwordHide
                            }
                            onClick={toggleHideRoutes}
                            disabled={!selectedRoutes?.length && isShowAll}
                        />
                    </ModernTooltip>
                    <div>
                        <DropdownV2
                            isOpen={isOpenExpand}
                            onClose={() => setIsOpenExpand(false)}
                            width="fullWidth"
                            trigger={
                                <ModernTooltip
                                    tooltip={texts.expand_tooltip}
                                    maxWidth={600}
                                    arrowOffset={8}
                                    position="top-start"
                                    tooltipOffset={8}
                                >
                                    <Icon
                                        icon={PriorityIcon.expand2}
                                        hoverable
                                        iconColor="primary-700"
                                        onClick={toggleExpandedDialog}
                                        dataTestId={DataTestIds.routesHeaderExpandedTrigger}
                                    />
                                </ModernTooltip>
                            }
                            usePortal
                            portalSelector={portalId}
                            openingSide={rtl ? 'rightSide' : 'leftSide'}
                        >
                            {expandMenuData.map((item, index) => {
                                return (
                                    <DropdownItem
                                        key={index}
                                        data={item}
                                        onSelect={onChangeExpandMode}
                                        isFocused={index === focusedIndex}
                                        itemBorder
                                    />
                                );
                            })}
                        </DropdownV2>
                    </div>
                </Flex>
            </Flex>
        </Flex>
    );
};
